import { computed, inject, Injectable, signal } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { SessionContextResponseDto } from '@zvoove-market/api';
import { ZvStorage } from '@zvoove-market/shared';
import { CookieConsentDialog } from './cookie-consent.dialog';

let gtagDefined = false;
const gaLoaded = signal<boolean>(false);
const STORAGE_KEY = 'gtm-consent';

declare global {
  interface Window {
    // eslint-disable-next-line @typescript-eslint/naming-convention
    GA_MEASUREMENT_ID: string | undefined;
    gtag: () => void;
    dataLayer: null | unknown[];
  }
}

@Injectable({
  providedIn: 'root',
})
export class GoogleAnalytics {
  #dialog = inject(MatDialog);
  readonly gaLoaded = computed<boolean>(() => gaLoaded());

  get tagId() {
    return window.GA_MEASUREMENT_ID ?? '';
  }

  constructor() {
    this.defineGtag();
    this.loadGaIfConsentGiven();
  }

  setConsent(consent: boolean) {
    if (consent) {
      this.grantConsent();
    } else {
      this.denyConsent();
    }
  }

  grantConsent() {
    // We need to update localStorage before initializing the GA, as the init check the localStorage.
    ZvStorage.local.setItem(STORAGE_KEY, 'true');
    this.loadGaIfConsentGiven();
  }

  denyConsent() {
    const oldValue = this.wasConsentGiven();
    // We first notify google about the denied consent and then disable it for us.
    gtag('consent', 'update', {
      analytics_storage: 'denied',
    });
    ZvStorage.local.setItem(STORAGE_KEY, 'false');
    if (oldValue) {
      window.location.reload();
    }
  }

  wasConsentConfigured() {
    return !!ZvStorage.local.getItem(STORAGE_KEY);
  }

  wasConsentGiven() {
    return ZvStorage.local.getItem(STORAGE_KEY) === 'true';
  }

  openConsentDialog() {
    this.#dialog.open(CookieConsentDialog, {
      hasBackdrop: false,
      disableClose: true,
      position: { bottom: '2em' },
    });
  }

  trackPageView(title: string, location: string) {
    gtag('event', 'page_view', {
      page_title: title,
      page_location: location,
    });
  }

  addUserId(userId: string | null) {
    gtag('set', 'user_id', userId!);
  }

  addUserProperties(session: SessionContextResponseDto) {
    const userProps: Record<string, string | undefined> = {
      zvooveCustomerId: session.customer?.id ?? '-',
      zvooveMandantId: session.mandant?.id ?? '-',
    };
    for (const role of session.globalRoles) {
      userProps[`role_g_${role}`] = 'true';
    }
    for (const role of session.mandant?.roles ?? []) {
      userProps[`role_m_${role}`] = 'true';
    }
    gtag('set', 'user_properties', userProps);
  }

  private defineGtag() {
    if (!gtagDefined) {
      gtagDefined = true;

      // eslint-disable-next-line @typescript-eslint/no-this-alias -- We need 'this' in the function below.
      const that = this;

      // ATTENTION!
      // google needs it exactly like this!
      // arrow function instead of 'function gtag(){...}' -> breaks it
      // ...args instead of 'arguments' -> breaks it
      window.dataLayer = window.dataLayer || [];
      window.gtag = function gtag() {
        // We check consent each time in here, in case consent was update in another browser tab.
        if (that.wasConsentGiven()) {
          // eslint-disable-next-line prefer-rest-params -- Rest params don't work here, google expects 'arguments'.
          window.dataLayer!.push(arguments);
        }
      };
    }
  }

  private loadGaIfConsentGiven() {
    if (!gaLoaded() && this.tagId && this.wasConsentGiven()) {
      // Set default consent to 'denied'.
      gtag('consent', 'default', {
        ad_storage: 'denied',
        ad_user_data: 'denied',
        ad_personalization: 'denied',
        analytics_storage: 'granted',
      });

      const gaScript = document.createElement('script');
      gaScript.async = true;
      gaScript.src = `https://www.googletagmanager.com/gtag/js?id=${this.tagId}`;
      document.head.appendChild(gaScript);

      gtag('js', new Date());
      gtag('config', this.tagId, {
        send_page_view: false, // This setting needs to be disabled for SPAs and be tracked manually.
      });

      gtag('consent', 'update', {
        analytics_storage: 'granted',
      });

      gaLoaded.set(true);
    }
  }
}
